import { trackCall } from "@/lib/track";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, CalendarDays, Wrench, Phone, MapPin } from "lucide-react";

export default function ConfirmPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, ok: false, data: null, error: null });

  useEffect(() => {
    api.post(`/appointments/confirm/${token}`)
      .then((r) => setState({ loading: false, ok: true, data: r.data, error: null }))
      .catch((err) => setState({
        loading: false,
        ok: false,
        data: null,
        error: formatApiErrorDetail(err.response?.data?.detail) || "Could not confirm booking",
      }));
  }, [token]);

  return (
    <div data-testid="confirm-page" className="bg-background min-h-[calc(100vh-4rem)]">
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        {state.loading ? (
          <div data-testid="confirm-loading" className="text-center text-zinc-400 font-mono text-xs uppercase tracking-widest py-20">
            Confirming your booking…
          </div>
        ) : state.ok ? (
          <div data-testid="confirm-success" className="text-center">
            <div className="inline-flex h-16 w-16 border border-brand-red bg-brand-red/10 grid place-items-center mb-8">
              <CheckCircle2 className="h-7 w-7 text-brand-red" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">
              Booking confirmed
            </div>
            <h1 className="mt-4 font-display uppercase text-5xl sm:text-6xl tracking-tighter text-white leading-[0.9]">
              See you tomorrow,<br />
              <span className="text-brand-red">{(state.data?.customer_name || "mate").split(" ")[0]}.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-xl mx-auto leading-relaxed">
              Thanks for confirming — we&apos;ve let the workshop know you&apos;ll be there.
              {state.data?.already_confirmed && " (You'd already confirmed earlier — no problem!)"}
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 text-left">
              <Info label="Service" Icon={Wrench} value={state.data?.service} />
              <Info label="When" Icon={CalendarDays} value={`${state.data?.preferred_date} · ${state.data?.preferred_time}`} />
              <Info label="Where" Icon={MapPin} value="46 Grove Rd, Glenorchy TAS 7010" />
              <Info label="Call" Icon={Phone} value="(03) 6288 7861" href="tel:+61362887861" />
            </div>
            <div className="mt-10">
              <Link
                to="/"
                data-testid="confirm-home-btn"
                className="inline-flex items-center gap-2 px-7 py-4 border border-white/20 text-white font-bold uppercase tracking-wider hover:border-brand-red hover:text-brand-red"
              >
                Back to home
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="confirm-failed" className="text-center">
            <div className="inline-flex h-16 w-16 border border-zinc-700 bg-zinc-900 grid place-items-center mb-8">
              <XCircle className="h-7 w-7 text-zinc-400" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-500">
              Could not confirm
            </div>
            <h1 className="mt-4 font-display uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
              {state.error}
            </h1>
            <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
              The link may have expired or the booking has been cancelled. Please call us on{" "}
              <a href="tel:+61362887861" onClick={() => trackCall("confirm_page")} className="text-brand-red underline">(03) 6288 7861</a>{" "}
              and we&apos;ll sort it out.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

const Info = ({ label, Icon, value, href }) => {
  const content = (
    <div className="bg-zinc-950 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-brand-red" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
      <div className="text-white font-display uppercase text-base tracking-tight">{value || "—"}</div>
    </div>
  );
  return href ? <a href={href} onClick={() => trackCall("confirm_page")} className="hover:bg-zinc-900 transition-colors">{content}</a> : content;
};
